import React, { useState, useEffect, useCallback } from 'react';
import {
  Smartphone, Tablet, Shield, Bell, FileText, RefreshCw, Trash2,
  CheckCircle, XCircle, AlertTriangle, Send, ChevronDown, ChevronUp,
  Monitor, Wifi, WifiOff, Lock, Unlock, Eye, Search, Filter, Download,
  ToggleLeft, ToggleRight, Clock, User, Activity
} from 'lucide-react';

const api = window.zignagenixAPI;

// ─── Shared Styles ──────────────────────────────────────────────────────────
const card = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,.07)', padding: 24, marginBottom: 20 };
const badge = (color) => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: 12,
  fontSize: 12, fontWeight: 600, background: color + '18', color,
});
const statBox = { textAlign: 'center', padding: '16px 12px', borderRadius: 10, background: '#f8fafc', flex: 1, minWidth: 120 };
const statNum = { fontSize: 28, fontWeight: 700, color: '#1e293b' };
const statLabel = { fontSize: 12, color: '#64748b', marginTop: 4 };
const tabBtn = (active) => ({
  padding: '10px 20px', border: 'none', borderBottom: active ? '3px solid #2563eb' : '3px solid transparent',
  background: 'none', color: active ? '#2563eb' : '#64748b', fontWeight: active ? 600 : 400,
  cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6,
  transition: 'all .15s',
});
const tableStyle = { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 };
const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0', background: '#f8fafc' };
const td = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9' };
const btnPrimary = { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 500, fontSize: 13 };
const btnDanger = { ...btnPrimary, background: '#ef4444' };
const btnGhost = { ...btnPrimary, background: 'transparent', color: '#64748b', border: '1px solid #e2e8f0' };
const inputStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%' };

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
}

function PlatformIcon({ platform }) {
  if (platform === 'ios') return <Smartphone size={16} style={{ color: '#3b82f6' }} />;
  if (platform === 'android') return <Tablet size={16} style={{ color: '#22c55e' }} />;
  return <Monitor size={16} style={{ color: '#94a3b8' }} />;
}


// ═══════════════════════════════════════════════════════════════════════════
//  DEVICE DASHBOARD TAB
// ═══════════════════════════════════════════════════════════════════════════

function DeviceDashboard() {
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [devList, devStats] = await Promise.all([
        api.getMobileDevices({ search, platform: platformFilter || undefined }),
        api.getMobileDeviceStats(),
      ]);
      setDevices(Array.isArray(devList) ? devList : []);
      setStats(devStats);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search, platformFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleRevoke(device) {
    if (!window.confirm(`Revoke device "${device.device_name}" for ${device.user_name}? This will force them to re-authenticate.`)) return;
    await api.revokeMobileDevice(device.id);
    load();
  }

  return (
    <div>
      {/* Stats Row */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={statBox}>
            <div style={statNum}>{stats.totalDevices}</div>
            <div style={statLabel}>Total Devices</div>
          </div>
          <div style={statBox}>
            <div style={statNum}>{stats.uniqueUsers}</div>
            <div style={statLabel}>Unique Users</div>
          </div>
          <div style={statBox}>
            <div style={{ ...statNum, color: '#22c55e' }}>{stats.activeToday}</div>
            <div style={statLabel}>Active Today</div>
          </div>
          <div style={statBox}>
            <div style={statNum}>{stats.activeThisWeek}</div>
            <div style={statLabel}>Active This Week</div>
          </div>
          <div style={statBox}>
            <div style={statNum}>{stats.byPlatform?.ios || 0}</div>
            <div style={statLabel}>iOS</div>
          </div>
          <div style={statBox}>
            <div style={statNum}>{stats.byPlatform?.android || 0}</div>
            <div style={statLabel}>Android</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
          <input placeholder="Search by user or device name..." style={{ ...inputStyle, paddingLeft: 32 }}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select style={{ ...inputStyle, width: 140 }} value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
          <option value="">All Platforms</option>
          <option value="ios">iOS</option>
          <option value="android">Android</option>
        </select>
        <button style={btnGhost} onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Device Table */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>User</th>
              <th style={th}>Device</th>
              <th style={th}>Platform</th>
              <th style={th}>Version</th>
              <th style={th}>Last Seen</th>
              <th style={th}>Registered</th>
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ ...td, textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading devices...</td></tr>
            ) : devices.length === 0 ? (
              <tr><td colSpan={7} style={{ ...td, textAlign: 'center', padding: 40, color: '#94a3b8' }}>No mobile devices registered yet</td></tr>
            ) : devices.map(d => (
              <tr key={d.id} style={{ transition: 'background .1s' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                <td style={td}>
                  <div style={{ fontWeight: 500 }}>{d.user_name || d.username}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{d.user_role}</div>
                </td>
                <td style={td}>{d.device_name}</td>
                <td style={td}><PlatformIcon platform={d.platform} /> {d.platform || 'Unknown'}</td>
                <td style={td}><span style={badge('#6366f1')}>{d.app_version || '—'}</span></td>
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {(Date.now() - new Date(d.last_seen).getTime()) < 3600000
                      ? <Wifi size={12} style={{ color: '#22c55e' }} />
                      : <WifiOff size={12} style={{ color: '#ef4444' }} />}
                    {timeAgo(d.last_seen)}
                  </div>
                </td>
                <td style={td}>{new Date(d.created_at).toLocaleDateString()}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button style={{ ...btnDanger, padding: '4px 10px', fontSize: 12 }} onClick={() => handleRevoke(d)}>
                    <Trash2 size={12} /> Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  2FA ENFORCEMENT TAB
// ═══════════════════════════════════════════════════════════════════════════

function TwoFactorPanel() {
  const [users, setUsers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([api.get2FAStatusAll(), api.get2FAPolicies()]);
      setUsers(Array.isArray(u) ? u : []);
      setPolicies(Array.isArray(p) ? p : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const enabledCount = users.filter(u => u.totpEnabled).length;
  const totalActive = users.length;

  async function handleReset(user) {
    if (!window.confirm(`Reset 2FA for ${user.displayName}? They will need to re-enroll their authenticator app.`)) return;
    await api.reset2FA(user.id);
    load();
  }

  async function handleEnforceRole(role, required) {
    await api.set2FAPolicy('role', role, required, 7);
    load();
  }

  async function handleEnforceGlobal(required) {
    await api.set2FAPolicy('global', null, required, 7);
    load();
  }

  const globalPolicy = policies.find(p => p.target_type === 'global');
  const rolePolicies = policies.filter(p => p.target_type === 'role');

  const filteredUsers = search
    ? users.filter(u => u.displayName?.toLowerCase().includes(search.toLowerCase()) || u.username?.toLowerCase().includes(search.toLowerCase()))
    : users;

  return (
    <div>
      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={statBox}>
          <div style={{ ...statNum, color: '#22c55e' }}>{enabledCount}</div>
          <div style={statLabel}>2FA Enabled</div>
        </div>
        <div style={statBox}>
          <div style={{ ...statNum, color: '#ef4444' }}>{totalActive - enabledCount}</div>
          <div style={statLabel}>Not Enrolled</div>
        </div>
        <div style={statBox}>
          <div style={statNum}>{Math.round(totalActive > 0 ? (enabledCount / totalActive) * 100 : 0)}%</div>
          <div style={statLabel}>Adoption Rate</div>
        </div>
      </div>

      {/* Global Policy */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Global 2FA Requirement</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {globalPolicy?.required ? 'All users must enroll in 2FA to use the mobile app' : 'Two-factor authentication is optional'}
          </div>
        </div>
        <button
          style={{ ...btnPrimary, background: globalPolicy?.required ? '#ef4444' : '#22c55e', display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => handleEnforceGlobal(!globalPolicy?.required)}>
          {globalPolicy?.required ? <><ToggleRight size={16} /> Disable</> : <><ToggleLeft size={16} /> Enable</>}
        </button>
      </div>

      {/* Role-Level Policies */}
      <div style={card}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Role-Level Enforcement</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['admin', 'physician', 'nurse', 'billing', 'receptionist', 'sysadmin'].map(role => {
            const enforced = rolePolicies.some(p => p.target_value === role && p.required);
            return (
              <button key={role} onClick={() => handleEnforceRole(role, !enforced)}
                style={{
                  padding: '6px 14px', borderRadius: 20, border: enforced ? '2px solid #22c55e' : '2px solid #e2e8f0',
                  background: enforced ? '#dcfce7' : '#fff', color: enforced ? '#166534' : '#475569',
                  fontWeight: 500, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}>
                {enforced ? <Lock size={12} /> : <Unlock size={12} />}
                {role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
              </button>
            );
          })}
        </div>
      </div>

      {/* User List */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
          <input placeholder="Search users..." style={{ ...inputStyle, paddingLeft: 32 }}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>User</th>
              <th style={th}>Role</th>
              <th style={th}>2FA Status</th>
              <th style={th}>Enrolled</th>
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ ...td, textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</td></tr>
            ) : filteredUsers.map(u => (
              <tr key={u.id}>
                <td style={td}>
                  <div style={{ fontWeight: 500 }}>{u.displayName}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{u.username}</div>
                </td>
                <td style={td}><span style={badge('#6366f1')}>{u.role}</span></td>
                <td style={td}>
                  {u.totpEnabled
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#22c55e', fontWeight: 500 }}><CheckCircle size={14} /> Enabled</span>
                    : <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#94a3b8' }}><XCircle size={14} /> Not enrolled</span>}
                </td>
                <td style={td}>{u.enrolledAt ? new Date(u.enrolledAt).toLocaleDateString() : '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {u.totpEnabled && (
                    <button style={{ ...btnDanger, padding: '4px 10px', fontSize: 12 }} onClick={() => handleReset(u)}>
                      Reset 2FA
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  MOBILE AUDIT LOG TAB
// ═══════════════════════════════════════════════════════════════════════════

function MobileAuditLog() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = new Date(Date.now() - days * 86400000).toISOString();
      const [evts, st] = await Promise.all([
        api.getMobileAuditLog({ action: actionFilter || undefined, startDate, limit: 200 }),
        api.getMobileAuditStats(days),
      ]);
      setEvents(Array.isArray(evts) ? evts : []);
      setStats(st);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [actionFilter, days]);

  useEffect(() => { load(); }, [load]);

  const actionColors = {
    'auth.login': '#22c55e', 'auth.login_failed': '#ef4444', 'auth.logout': '#6366f1',
    'phi.view': '#3b82f6', 'phi.export': '#f59e0b', 'phi.message_read': '#8b5cf6',
    'integrity.rooted_device_detected': '#ef4444', 'auth.auto_logout': '#94a3b8',
    'auth.password_changed': '#0ea5e9', 'settings.device_signed_out': '#f97316',
  };

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={statBox}>
            <div style={statNum}>{stats.totalEvents}</div>
            <div style={statLabel}>Events ({stats.period})</div>
          </div>
          <div style={statBox}>
            <div style={{ ...statNum, color: '#ef4444' }}>{stats.failedLogins}</div>
            <div style={statLabel}>Failed Logins</div>
          </div>
          <div style={statBox}>
            <div style={{ ...statNum, color: stats.rootedDeviceDetections > 0 ? '#ef4444' : '#22c55e' }}>{stats.rootedDeviceDetections}</div>
            <div style={statLabel}>Rooted Devices</div>
          </div>
          <div style={statBox}>
            <div style={{ ...statNum, color: '#f59e0b' }}>{stats.phiExports}</div>
            <div style={statLabel}>PHI Exports</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <select style={{ ...inputStyle, width: 200 }} value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
          <option value="">All Events</option>
          <option value="auth.login">Logins</option>
          <option value="auth.login_failed">Failed Logins</option>
          <option value="auth.logout">Logouts</option>
          <option value="phi.view">PHI Views</option>
          <option value="phi.export">PHI Exports</option>
          <option value="integrity.rooted_device_detected">Rooted Device</option>
        </select>
        <select style={{ ...inputStyle, width: 130 }} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button style={btnGhost} onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Event Table */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Time</th>
              <th style={th}>User</th>
              <th style={th}>Event</th>
              <th style={th}>Details</th>
              <th style={th}>IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ ...td, textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading audit log...</td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={5} style={{ ...td, textAlign: 'center', padding: 40, color: '#94a3b8' }}>No mobile events found for this period</td></tr>
            ) : events.map(e => {
              let details = {};
              try { details = JSON.parse(e.details || '{}'); } catch { details = {}; }
              return (
                <tr key={e.id}>
                  <td style={td}>
                    <div style={{ fontSize: 12 }}>{new Date(e.created_at).toLocaleString()}</div>
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: 500 }}>{e.user_name || '—'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{e.username}</div>
                  </td>
                  <td style={td}>
                    <span style={badge(actionColors[e.action] || '#64748b')}>{e.action}</span>
                  </td>
                  <td style={td}>
                    <div style={{ fontSize: 12, color: '#475569', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.resource_id ? `Resource: ${e.resource_id}` : ''}
                      {details.reason ? ` — ${details.reason}` : ''}
                      {details.device ? ` (${details.device})` : ''}
                    </div>
                  </td>
                  <td style={td}><span style={{ fontSize: 12, color: '#94a3b8' }}>{e.ip_address}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  PUSH NOTIFICATIONS TAB
// ═══════════════════════════════════════════════════════════════════════════

function PushNotificationsPanel() {
  const [config, setConfig] = useState([]);
  const [subscriptions, setSubs] = useState([]);
  const [pushLog, setPushLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, l] = await Promise.all([
        api.getPushConfig(),
        api.getPushSubscriptions(),
        api.getPushLog(50),
      ]);
      setConfig(Array.isArray(c) ? c : []);
      setSubs(Array.isArray(s) ? s : []);
      setPushLog(Array.isArray(l) ? l : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleEvent(eventType, currentEnabled) {
    const item = config.find(c => c.event_type === eventType);
    if (!item) return;
    await api.updatePushConfig(eventType, !currentEnabled, item.title_template, item.body_template);
    load();
  }

  async function handleSendTest(userId) {
    const result = await api.sendTestPush(userId);
    if (result?.error) {
      alert(result.message);
    } else {
      alert(result.simulated
        ? `Test push simulated for ${result.devices} device(s). Configure FCM_SERVER_KEY for live delivery.`
        : `Test push sent to ${result.sent} device(s).`);
    }
    load();
  }

  async function saveTemplate(eventType, title, body) {
    const item = config.find(c => c.event_type === eventType);
    await api.updatePushConfig(eventType, item?.enabled ?? true, title, body);
    setEditingEvent(null);
    load();
  }

  const eventLabels = {
    appointment_reminder: 'Appointment Reminders',
    new_message: 'New Messages',
    lab_result_ready: 'Lab Results Ready',
    schedule_change: 'Schedule Changes',
    urgent_alert: 'Urgent Alerts',
    patient_checkin: 'Patient Check-In',
  };

  return (
    <div>
      {/* Subscription Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={statBox}>
          <div style={statNum}>{subscriptions.filter(s => s.is_active).length}</div>
          <div style={statLabel}>Active Subscriptions</div>
        </div>
        <div style={statBox}>
          <div style={statNum}>{new Set(subscriptions.map(s => s.user_id)).size}</div>
          <div style={statLabel}>Users with Push</div>
        </div>
        <div style={statBox}>
          <div style={statNum}>{config.filter(c => c.enabled).length}/{config.length}</div>
          <div style={statLabel}>Events Enabled</div>
        </div>
      </div>

      {/* Event Configuration */}
      <div style={card}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Push Event Configuration</div>
        {loading ? <div style={{ color: '#94a3b8' }}>Loading...</div> : config.map(c => (
          <div key={c.event_type} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
            <button onClick={() => toggleEvent(c.event_type, c.enabled)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              {c.enabled
                ? <ToggleRight size={24} style={{ color: '#22c55e' }} />
                : <ToggleLeft size={24} style={{ color: '#d1d5db' }} />}
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: 14 }}>{eventLabels[c.event_type] || c.event_type}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                Title: {c.title_template} — Body: {c.body_template?.slice(0, 60)}...
              </div>
            </div>
            <button style={btnGhost} onClick={() => setEditingEvent(editingEvent === c.event_type ? null : c.event_type)}>
              Edit Template
            </button>
            {editingEvent === c.event_type && (
              <TemplateEditor
                title={c.title_template}
                body={c.body_template}
                onSave={(t, b) => saveTemplate(c.event_type, t, b)}
                onCancel={() => setEditingEvent(null)}
              />
            )}
          </div>
        ))}
      </div>

      {/* Active Subscriptions */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Active Subscriptions</div>
        </div>
        <div style={{ maxHeight: 300, overflow: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>User</th>
                <th style={th}>Platform</th>
                <th style={th}>Status</th>
                <th style={th}>Updated</th>
                <th style={{ ...th, textAlign: 'right' }}>Test</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map(s => (
                <tr key={s.id}>
                  <td style={td}>{s.user_name || s.username}</td>
                  <td style={td}><PlatformIcon platform={s.platform} /> {s.platform}</td>
                  <td style={td}>
                    {s.is_active
                      ? <span style={{ color: '#22c55e', fontWeight: 500 }}>Active</span>
                      : <span style={{ color: '#ef4444' }}>Inactive</span>}
                  </td>
                  <td style={td}>{timeAgo(s.updated_at)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button style={{ ...btnPrimary, padding: '4px 10px', fontSize: 12 }} onClick={() => handleSendTest(s.user_id)}>
                      <Send size={12} /> Test
                    </button>
                  </td>
                </tr>
              ))}
              {subscriptions.length === 0 && (
                <tr><td colSpan={5} style={{ ...td, textAlign: 'center', padding: 30, color: '#94a3b8' }}>No push subscriptions yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Push Log (collapsible) */}
      <div style={card}>
        <button style={{ ...btnGhost, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          onClick={() => setShowLog(!showLog)}>
          <span style={{ fontWeight: 600 }}>Recent Push Log ({pushLog.length})</span>
          {showLog ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showLog && (
          <div style={{ marginTop: 12, maxHeight: 300, overflow: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={th}>Time</th>
                  <th style={th}>User</th>
                  <th style={th}>Event</th>
                  <th style={th}>Title</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pushLog.map(l => (
                  <tr key={l.id}>
                    <td style={td}>{new Date(l.created_at).toLocaleString()}</td>
                    <td style={td}>{l.user_name || '—'}</td>
                    <td style={td}><span style={badge('#6366f1')}>{l.event_type}</span></td>
                    <td style={td}>{l.title}</td>
                    <td style={td}>
                      <span style={badge(l.status === 'sent' ? '#22c55e' : l.status === 'simulated' ? '#f59e0b' : '#ef4444')}>
                        {l.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateEditor({ title: initTitle, body: initBody, onSave, onCancel }) {
  const [title, setTitle] = useState(initTitle || '');
  const [body, setBody] = useState(initBody || '');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, boxShadow: '0 8px 32px rgba(0,0,0,.2)' }}>
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>Edit Push Template</div>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#475569' }}>Title Template</label>
        <input style={{ ...inputStyle, marginBottom: 12, marginTop: 4 }} value={title} onChange={(e) => setTitle(e.target.value)} />
        <label style={{ fontSize: 13, fontWeight: 500, color: '#475569' }}>Body Template</label>
        <textarea style={{ ...inputStyle, marginTop: 4, minHeight: 80, resize: 'vertical' }} value={body} onChange={(e) => setBody(e.target.value)} />
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, marginBottom: 16 }}>
          Use {'{{variable_name}}'} for dynamic values. Available: patient_name, time, sender_name, preview, date, alert_message
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btnGhost} onClick={onCancel}>Cancel</button>
          <button style={btnPrimary} onClick={() => onSave(title, body)}>Save Template</button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function MobileManagementPage() {
  const [activeTab, setActiveTab] = useState('devices');

  const tabs = [
    { id: 'devices',  label: 'Devices',           icon: Smartphone },
    { id: '2fa',      label: '2FA Enforcement',   icon: Shield },
    { id: 'audit',    label: 'Mobile Audit Log',  icon: FileText },
    { id: 'push',     label: 'Push Notifications', icon: Bell },
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Smartphone size={24} /> Mobile Management
        </h1>
        <p style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>
          Manage mobile app devices, two-factor authentication, audit trail, and push notifications.
        </p>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: 24, gap: 4 }}>
        {tabs.map(tab => (
          <button key={tab.id} style={tabBtn(activeTab === tab.id)} onClick={() => setActiveTab(tab.id)}>
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'devices' && <DeviceDashboard />}
      {activeTab === '2fa' && <TwoFactorPanel />}
      {activeTab === 'audit' && <MobileAuditLog />}
      {activeTab === 'push' && <PushNotificationsPanel />}
    </div>
  );
}
